// ─── Dogs Query & Mutation Hooks ────────────────────────────────────
// React Query hooks for dog profile data.
// These replace the useEffect+useState pattern in DogsContext.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchDogProfiles,
  createDogProfileAPI,
  updateDogProfileAPI,
} from '@/lib/api/dogs';
import type { DogProfile } from '@/types/dog-profile';

/** Fetch the current user's dog profiles. Enabled only when a token is available. */
export function useDogProfiles(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.dogs.profiles,
    queryFn: () => fetchDogProfiles(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 min — rarely changes
  });
}

/** Create a new dog profile. Invalidates the profiles list on success. */
export function useCreateDogProfile(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: Omit<DogProfile, 'id' | 'created_at' | 'updated_at'>) => {
      if (!token) throw new Error('No session');
      return createDogProfileAPI(token, profile);
    },
    onSuccess: (newProfile) => {
      // Optimistically add to the list
      queryClient.setQueryData<DogProfile[]>(queryKeys.dogs.profiles, (old) => {
        return old ? [...old, newProfile] : [newProfile];
      });
    },
  });
}

/** Update an existing dog profile. Updates the cache on success. */
export function useUpdateDogProfile(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: DogProfile) => {
      if (!token) throw new Error('No session');
      return updateDogProfileAPI(token, profile);
    },
    onSuccess: (updatedProfile) => {
      // Update the profile in the list cache
      queryClient.setQueryData<DogProfile[]>(queryKeys.dogs.profiles, (old) => {
        return old ? old.map(d => d.id === updatedProfile.id ? updatedProfile : d) : [updatedProfile];
      });
      // Also invalidate any individual profile caches
      if (updatedProfile.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dogs.profile(updatedProfile.id) });
      }
    },
  });
}